import React from 'react';
import { createColumnHelper } from '@tanstack/react-table';
import { Spinner } from 'react-bootstrap';
import { formatNumberToKM } from '../../sequence/helpers/helper';
import {
  NameCell,
  CompanyCell,
  SocialsCell,
  TextCell,
  ExperienceCell,
} from '../../v2-lead/components/table/cell-renderers';
import EmailListWithCopy from '../../v2-lead/components/profile-modal/shared/email-list-with-copy';
import PhoneListWithCopy from '../../v2-lead/components/profile-modal/shared/phone-list-with-copy';
import {
  WORK_EMAIL_REVEAL_TYPES,
  PHONE_REVEAL_TYPES,
} from '../../v2-lead/helpers/helper';
import ProspectTagsRow from '../../../shared/design-system/components/molecules/prospect-tags-row';
import type { V2Lead } from '../../v2-lead/types';
import {
  getPlatformType,
  ICON_MAP,
} from '../../v2-lead/components/table/cell-renderers/socials-cell';

type PeopleStatusColumn = {
  header: string;
  size?: number;
  cell: (record: Record<string, any>) => React.ReactNode;
};

const EMPTY_TEXT = <TextCell value="-" />;

const joinParts = (...parts: (string | null | undefined)[]): string | null => {
  const s = parts
    .filter((p) => p != null && String(p).trim() !== '')
    .join(', ');
  return s || null;
};

const normalizeEmails = (
  raw: unknown,
): { email: string; shVerificationStatus?: string }[] => {
  if (!raw) return [];
  if (!Array.isArray(raw)) return [];
  return raw
    .map((item) => {
      if (typeof item === 'string' && item.trim()) {
        return { email: item.trim() };
      }
      if (item && typeof item === 'object' && 'email' in item) {
        const e = String((item as { email?: string }).email || '').trim();
        if (!e) return null;
        const status = (item as { shVerificationStatus?: string })
          .shVerificationStatus;
        return status
          ? { email: e, shVerificationStatus: status }
          : { email: e };
      }
      return null;
    })
    .filter(Boolean) as { email: string; shVerificationStatus?: string }[];
};

const normalizePhones = (raw: unknown): string[] => {
  if (!raw) return [];
  if (!Array.isArray(raw)) return [];
  return raw
    .map((item) => {
      if (typeof item === 'string') return item.trim() || null;
      if (item && typeof item === 'object') {
        if ('number' in item) {
          return (
            String((item as { number?: string }).number || '').trim() || null
          );
        }
        if ('phone' in item) {
          return (
            String((item as { phone?: string }).phone || '').trim() || null
          );
        }
      }
      return null;
    })
    .filter(Boolean) as string[];
};

const yesNo = (v: unknown): React.ReactNode => {
  if (v === true) return <span>Yes</span>;
  if (v === false) return <span>No</span>;
  return EMPTY_TEXT;
};

const firstArrayString = (raw: unknown): string | null => {
  if (!raw || !Array.isArray(raw) || raw.length === 0) return null;
  const first = raw[0];
  if (first == null || first === '') return null;
  return typeof first === 'string' ? first : String(first);
};

const formatCurrencyToKM = (raw: unknown): string | null => {
  if (raw === null || raw === undefined || raw === '') return null;
  const n =
    typeof raw === 'number'
      ? raw
      : Number(String(raw).replace(/[^0-9.-]/g, ''));
  if (!Number.isFinite(n)) {
    return typeof raw === 'string' && raw.trim() ? raw.trim() : null;
  }
  return `$${formatNumberToKM(n)}`;
};

const extractDomainDisplay = (url: unknown): string | null => {
  if (!url || typeof url !== 'string' || !url.trim()) return null;
  try {
    const s = url.trim();
    const parsed = new URL(s.startsWith('http') ? s : `https://${s}`);
    return parsed.hostname.replace(/^www\./, '');
  } catch {
    return url.trim() || null;
  }
};

const asLead = (record: Record<string, any>): V2Lead => record as V2Lead;

const nameCell = ({
  full_name: fullNameField,
  first_name,
  last_name,
  name,
  picture_url,
  pictureUrl,
}: Record<string, any>) => {
  const fullName =
    fullNameField || joinParts(first_name, last_name) || name || null;
  if (!fullName) return EMPTY_TEXT;
  return (
    <NameCell
      fullName={fullName}
      pictureUrl={picture_url ?? pictureUrl ?? null}
    />
  );
};

const emailsCell = (record: Record<string, any>) => {
  const { emails, email, isRevealing, revealType } = record;
  const normalized = normalizeEmails(emails ?? email);
  const hasData = normalized.length > 0;
  const isRevealingThis =
    isRevealing === true &&
    (revealType === undefined ||
      WORK_EMAIL_REVEAL_TYPES.includes(revealType as any));
  if (!hasData && isRevealingThis) {
    return (
      <div className="people-company-enriching">
        <Spinner animation="border" className="enriching-spinner" />
        <span className="revealing-text">Enriching...</span>
      </div>
    );
  }
  return <EmailListWithCopy emails={normalized} showVerification />;
};

const phonesCell = (record: Record<string, any>) => {
  const { phones, phone, isRevealing, revealType } = record;
  const normalized = normalizePhones(phones ?? phone);
  const hasData = normalized.length > 0;
  const isRevealingThis =
    isRevealing === true &&
    (revealType === undefined ||
      PHONE_REVEAL_TYPES.includes(revealType as any));
  if (!hasData && isRevealingThis) {
    return (
      <div className="people-company-enriching">
        <Spinner animation="border" className="enriching-spinner" />
        <span className="revealing-text">Enriching...</span>
      </div>
    );
  }
  return <PhoneListWithCopy phones={normalized} />;
};

const socialsCell = (record: Record<string, any>) => (
  <SocialsCell lead={asLead(record)} />
);

const companyCell = (record: Record<string, any>) => (
  <CompanyCell lead={asLead(record)} />
);

const locationCell = ({
  location,
  location_city,
  location_state,
  location_country,
}: Record<string, any>) => {
  // Backend may send either a pre-joined `location` string or split fields.
  if (typeof location === 'string' && location.trim()) {
    return <TextCell value={location.trim()} />;
  }
  const loc = joinParts(location_city, location_state, location_country);
  if (!loc) return EMPTY_TEXT;
  return <TextCell value={loc} />;
};

const hqLocationCell = ({
  company_hq_location,
  company_hq_city,
  company_hq_state,
  company_hq_country,
}: Record<string, any>) => {
  if (typeof company_hq_location === 'string' && company_hq_location.trim()) {
    return <TextCell value={company_hq_location.trim()} />;
  }
  const loc = joinParts(company_hq_city, company_hq_state, company_hq_country);
  if (!loc) return EMPTY_TEXT;
  return <TextCell value={loc} />;
};

const normalizeCompanySocialEntry = (value: unknown): string | null => {
  if (!value) return null;
  if (typeof value === 'string') {
    const s = value.trim();
    return s || null;
  }
  if (typeof value === 'object' && value !== null && 'url' in value) {
    const s = String((value as Record<string, unknown>).url || '').trim();
    return s || null;
  }
  return null;
};

/** Matches v2-lead `company_socials` column: platform icons, link opens URL. */
const companySocialsCell = ({
  company_socials,
  social_urls,
}: Record<string, any>) => {
  const raw = company_socials ?? social_urls;
  if (!raw || !Array.isArray(raw) || raw.length === 0) {
    return <div className="v2-lead-socials-cell">-</div>;
  }
  const urls = raw
    .map((entry) => normalizeCompanySocialEntry(entry))
    .filter((u): u is string => Boolean(u));
  const unique = urls.filter(
    (url, index, arr) =>
      arr.findIndex((u) => u.toLowerCase() === url.toLowerCase()) === index,
  );
  if (unique.length === 0) {
    return <div className="v2-lead-socials-cell">-</div>;
  }
  return (
    <div className="v2-lead-socials-cell">
      {unique.map((entry) => {
        const IconComponent =
          ICON_MAP[getPlatformType(entry)] || ICON_MAP.generic;
        return (
          <a
            key={entry}
            href={entry}
            target="_blank"
            rel="noreferrer"
            className="v2-lead-social-link"
            onClick={(e) => e.stopPropagation()}
          >
            <IconComponent width={20} height={20} />
          </a>
        );
      })}
    </div>
  );
};

const skillsOrTagsCell = (
  record: Record<string, any>,
  field: 'skills' | 'tags',
) => {
  const { skills, tags: tagsFromRecord } = record;
  const raw = field === 'skills' ? skills : tagsFromRecord;
  if (!raw || !Array.isArray(raw) || raw.length === 0) return EMPTY_TEXT;
  const tagRows = raw.map((item: any, i: number) => {
    if (item && typeof item === 'object' && 'name' in item && 'id' in item) {
      return { id: item.id, name: String(item.name) };
    }
    if (typeof item === 'string') {
      return { id: i, name: item };
    }
    return { id: i, name: String(item) };
  });
  return (
    <ProspectTagsRow tags={tagRows} maxTags={1} automaticallyShowTooltip />
  );
};

const PEOPLE_STATUS_COLUMN_MAP: Record<string, PeopleStatusColumn> = {
  name: { header: 'Name', size: 240, cell: nameCell },
  full_name: { header: 'Name', size: 240, cell: nameCell },

  // email: { header: 'Emails', size: 180, cell: emailsCell },
  emails: { header: 'Emails', size: 180, cell: emailsCell },

  job_title: {
    header: 'Job Title',
    size: 160,
    cell: ({ job_title }) => <TextCell value={job_title} />,
  },

  past_companies: {
    header: 'Past Companies',
    size: 220,
    cell: ({ past_companies }) =>
      past_companies?.length ? (
        <ExperienceCell experience={past_companies} />
      ) : (
        EMPTY_TEXT
      ),
  },

  phones: { header: 'Phone Number', size: 180, cell: phonesCell },

  department: {
    header: 'Department',
    size: 160,
    cell: ({ department }) => <TextCell value={department} />,
  },

  socials: { header: 'Socials', size: 164, cell: socialsCell },
  social_link: { header: 'Socials', size: 164, cell: socialsCell },

  location: { header: 'Location', size: 220, cell: locationCell },

  industry: {
    header: 'Industry',
    size: 200,
    cell: ({ company_industry, industry: industryField }) => (
      <TextCell value={company_industry ?? industryField} />
    ),
  },
  company_industry: {
    header: 'Company Industry',
    size: 200,
    cell: ({ company_industry }) => <TextCell value={company_industry} />,
  },

  management_level: {
    header: 'Management Level',
    size: 180,
    cell: ({ management_level }) => <TextCell value={management_level} />,
  },

  decision_maker: {
    header: 'Decision Maker',
    size: 140,
    cell: ({ decision_maker }) => yesNo(decision_maker),
  },

  job_changed_on: {
    header: 'Job Changed On',
    size: 180,
    cell: ({ jobs_changed_within, job_changed_on }) => (
      <TextCell value={jobs_changed_within ?? job_changed_on} />
    ),
  },
  jobs_changed_within: {
    header: 'Job Changed On',
    size: 180,
    cell: ({ jobs_changed_within }) => <TextCell value={jobs_changed_within} />,
  },

  skills: {
    header: 'Skills',
    size: 220,
    cell: (record) => skillsOrTagsCell(record, 'skills'),
  },
  tags: {
    header: 'Tags',
    size: 220,
    cell: (record) => skillsOrTagsCell(record, 'tags'),
  },

  experience_at_current_company: {
    header: 'Current Company Experience',
    size: 200,
    cell: ({ experience_at_current_company }) => (
      <TextCell value={experience_at_current_company} />
    ),
  },

  total_experience: {
    header: 'Years of Experience',
    size: 180,
    cell: ({ total_experience }) => <TextCell value={total_experience} />,
  },

  active_experiences: {
    header: 'Active Experiences',
    size: 220,
    cell: ({ active_experiences }) =>
      active_experiences?.length ? (
        <ExperienceCell experience={active_experiences} />
      ) : (
        EMPTY_TEXT
      ),
  },

  current_role_tenure_months: {
    header: 'Tenure in Current Role',
    size: 200,
    cell: ({ current_role_tenure_months: tenureMonths }) => {
      if (
        tenureMonths === null ||
        tenureMonths === undefined ||
        tenureMonths === ''
      ) {
        return EMPTY_TEXT;
      }
      const num =
        typeof tenureMonths === 'number' ? tenureMonths : Number(tenureMonths);
      if (!Number.isFinite(num)) return <TextCell value={tenureMonths} />;
      return <TextCell value={num.toLocaleString()} />;
    },
  },

  projected_base_salary_dollars: {
    header: 'Projected Salary',
    size: 200,
    cell: ({ projected_base_salary_dollars: salary }) => {
      const v = formatCurrencyToKM(salary);
      if (!v) return EMPTY_TEXT;
      return <TextCell value={v} />;
    },
  },

  education_degrees: {
    header: 'Degree',
    size: 200,
    cell: ({ education_degrees }) => {
      const first = firstArrayString(education_degrees);
      if (!first) return EMPTY_TEXT;
      return <TextCell value={first} />;
    },
  },
  education_degree: {
    header: 'Degree',
    size: 200,
    cell: ({ education_degree }) => <TextCell value={education_degree} />,
  },
  institution_name: {
    header: 'School',
    size: 200,
    cell: ({ institution_name }) => {
      const first = firstArrayString(institution_name);
      if (!first) return EMPTY_TEXT;
      return <TextCell value={first} />;
    },
  },
  education_school: {
    header: 'School',
    size: 200,
    cell: ({ education_school }) => <TextCell value={education_school} />,
  },

  linkedin_followers_count: {
    header: 'Followers',
    size: 160,
    cell: ({ linkedin_followers_count: followerCount }) => {
      if (
        followerCount === null ||
        followerCount === undefined ||
        followerCount === ''
      ) {
        return EMPTY_TEXT;
      }
      const num =
        typeof followerCount === 'number'
          ? followerCount
          : Number(followerCount);
      if (!Number.isFinite(num)) return <TextCell value={followerCount} />;
      return <TextCell value={formatNumberToKM(num)} />;
    },
  },

  linkedin_url: {
    header: 'LinkedIn',
    size: 200,
    cell: ({ linkedin_url: linkedIn }) => {
      if (!linkedIn || !String(linkedIn).trim()) return EMPTY_TEXT;
      return <TextCell value={linkedIn} redirectUrl={linkedIn} />;
    },
  },

  first_name: {
    header: 'First Name',
    size: 160,
    cell: ({ first_name }) => <TextCell value={first_name} />,
  },
  last_name: {
    header: 'Last Name',
    size: 160,
    cell: ({ last_name }) => <TextCell value={last_name} />,
  },

  company_name: { header: 'Company Name', size: 220, cell: companyCell },

  company_website: {
    header: 'Company Website',
    size: 200,
    cell: ({ company_website: website }) => {
      if (!website || !String(website).trim()) return EMPTY_TEXT;
      return <TextCell value={website} redirectUrl={website} />;
    },
  },

  company_size_range: {
    header: 'Company Size',
    size: 180,
    cell: ({ company_size_range }) => <TextCell value={company_size_range} />,
  },

  company_annual_revenue: {
    header: 'Company Revenue',
    size: 180,
    cell: ({ company_annual_revenue }) => {
      const v = formatCurrencyToKM(company_annual_revenue);
      if (!v) return EMPTY_TEXT;
      return <TextCell value={v} />;
    },
  },

  company_founded_year: {
    header: 'Company Founded Year',
    size: 180,
    cell: ({ company_founded_year }) => (
      <TextCell value={company_founded_year} />
    ),
  },

  company_is_b2b: {
    header: 'Is B2B',
    size: 120,
    cell: ({ company_is_b2b }) => yesNo(company_is_b2b),
  },

  company_hq_location: {
    header: 'Company HQ Location',
    size: 220,
    cell: hqLocationCell,
  },

  company_domain: {
    header: 'Company Domain',
    size: 200,
    cell: ({ company_domain, company_website }) => {
      const raw = company_domain ?? company_website;
      const display = extractDomainDisplay(raw);
      const redirect = company_website ?? company_domain;
      if (!display) return EMPTY_TEXT;
      return (
        <TextCell
          value={display}
          redirectUrl={
            redirect && String(redirect).trim() ? redirect : undefined
          }
        />
      );
    },
  },

  company_socials: {
    header: 'Company Socials',
    size: 220,
    cell: companySocialsCell,
  },
  social_urls: {
    header: 'Company Socials',
    size: 220,
    cell: companySocialsCell,
  },

  funding_amount: {
    header: 'Funding Amount',
    size: 180,
    cell: ({
      company_last_funding_round_amount_raised,
      funding_amount: fundingAmt,
    }) => {
      const v = formatCurrencyToKM(
        company_last_funding_round_amount_raised ?? fundingAmt,
      );
      if (!v) return EMPTY_TEXT;
      return <TextCell value={v} />;
    },
  },
  funding_date: {
    header: 'Funding Date',
    size: 180,
    cell: ({ company_last_funding_date, funding_date }) => (
      <TextCell value={company_last_funding_date ?? funding_date} />
    ),
  },
  company_last_funding_round_amount_raised: {
    header: 'Last Raised Amount',
    size: 180,
    cell: ({ company_last_funding_round_amount_raised: raised }) => {
      const v = formatCurrencyToKM(raised);
      if (!v) return EMPTY_TEXT;
      return <TextCell value={v} />;
    },
  },
  company_last_funding_date: {
    header: 'Last Funding Date',
    size: 180,
    cell: ({ company_last_funding_date }) => (
      <TextCell value={company_last_funding_date} />
    ),
  },

  profile_summary: {
    header: 'Profile Summary',
    size: 260,
    cell: ({ profile_summary }) => <TextCell value={profile_summary} />,
  },

  location_city: {
    header: 'Location City',
    size: 160,
    cell: ({ location_city }) => <TextCell value={location_city} />,
  },
  location_state: {
    header: 'Location State',
    size: 160,
    cell: ({ location_state }) => <TextCell value={location_state} />,
  },
  location_country: {
    header: 'Location Country',
    size: 160,
    cell: ({ location_country }) => <TextCell value={location_country} />,
  },
};

const columnHelper = createColumnHelper<Record<string, any>>();

export function buildPeopleStatusColumns(apiColumns: string[]) {
  return apiColumns
    .map((key) => {
      const def = PEOPLE_STATUS_COLUMN_MAP[key];
      if (!def) return null;
      return columnHelper.display({
        id: key,
        header: def.header,
        size: def.size ?? 180,
        cell: ({ row: { original } }) => def.cell(original),
      });
    })
    .filter((col): col is NonNullable<typeof col> => col != null);
}
